import React, { useState } from 'react'
import { FiUser, FiLock, FiEye, FiEyeOff, FiLoader, FiAlertCircle } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'

const AuthLogin: React.FC = () => {
  const { login, isLoading } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password.trim()) {
      setError('Preencha usuário e senha.')
      return
    }
    setError(null)
    try {
      const ok = await login(username.trim(), password)
      if (!ok) setError('Credenciais inválidas. Verifique seu usuário e senha.')
    } catch {
      setError('Erro de conexão. Tente novamente.')
    }
  }

  const hasError = !!error

  return (
    <div className="relative min-h-screen w-full bg-background flex items-center justify-center px-5 py-10 overflow-hidden">
      {/* Base opaco (fallback enquanto a foto carrega; cobre o pontilhismo global). */}
      <div aria-hidden className="absolute inset-0 bg-background" />
      {/* Foto de vitrine Tupperware cobrindo a tela — o fundo de imagem. */}
      <img
        aria-hidden
        src="/login-bg.webp"
        alt=""
        draggable={false}
        className="login-bg-kenburns pointer-events-none absolute inset-0 h-full w-full object-cover select-none opacity-[0.10]"
      />
      {/* Camada de COR por cima da imagem: scrim do tema (contraste/legibilidade,
          mais forte no dark) + tint verde sutil da marca pra coesão. O vidro
          fosco do card refrata tudo isso por trás. */}
      <div aria-hidden className="absolute inset-0 bg-background/10 dark:bg-background/25" />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(900px 500px at 50% 0%, hsl(var(--primary) / 0.12), transparent 60%)',
        }}
      />
      {/* Blobs de cor da marca nas extremidades, POR CIMA da foto — detalhe Liquid Glass. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-primary/25 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -right-24 h-[26rem] w-[26rem] rounded-full bg-primary/15 blur-3xl"
      />

      <main className="relative w-full max-w-[420px]">
        {/* Card de vidro fosco: fundo translúcido + backdrop-blur, borda com
            brilho especular no topo (inset highlight) e sombra externa suave.
            Tokens shadcn preservados; opacidades ajustadas por light/dark. */}
        <section
          className="bg-card/80 dark:bg-card/60 text-card-foreground border border-white/50 dark:border-white/10 rounded-3xl backdrop-blur-xl
                     shadow-[0_8px_40px_rgba(13,39,80,0.12),inset_0_1px_0_rgba(255,255,255,0.55)]
                     dark:shadow-[0_8px_40px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.08)]"
        >
          {/* Header: só o logo (a identidade já fala por si) */}
          {/* Duas <img> em paralelo + toggle por classe Tailwind .dark — evita
              flicker que useTheme() teria, e o navegador só baixa a imagem
              visível (display:none não dispara fetch da img escondida). */}
          <header className="px-6 pt-8 pb-2 flex justify-center">
            {/* Dimensionamento por LARGURA, não por altura: a marca do Planalto
                tem proporção 3,95:1 contra 2,81:1 da matriz — manter `h-20`
                daria 316px de largura e estouraria o card no celular. */}
            <img
              src="/planalto-animado-light.gif"
              alt="Distribuição Planalto"
              className="w-[260px] max-w-full h-auto select-none block dark:hidden"
              draggable={false}
            />
            <img
              src="/planalto-animado-dark.gif"
              alt="Distribuição Planalto"
              className="w-[260px] max-w-full h-auto select-none hidden dark:block"
              draggable={false}
            />
            <h1 className="sr-only">Entrar no CRM</h1>
          </header>

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate className="px-6 pt-4 pb-6 space-y-5">
            {hasError && (
              <div
                role="alert"
                aria-live="polite"
                className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2.5 text-sm"
              >
                <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="username" className="block text-sm font-medium text-foreground">
                Usuário
              </label>
              <div className="relative">
                <FiUser
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                />
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  aria-invalid={hasError || undefined}
                  disabled={isLoading}
                  className="block w-full rounded-xl border border-white/60 dark:border-white/10 bg-white/50 dark:bg-white/[0.06] backdrop-blur-md text-foreground placeholder:text-muted-foreground/70
                             pl-9 pr-3 py-2.5 text-sm
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-card
                             disabled:opacity-60 disabled:cursor-not-allowed
                             transition-colors"
                  placeholder="seu.usuario"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                Senha
              </label>
              <div className="relative">
                <FiLock
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={hasError || undefined}
                  disabled={isLoading}
                  className="block w-full rounded-xl border border-white/60 dark:border-white/10 bg-white/50 dark:bg-white/[0.06] backdrop-blur-md text-foreground placeholder:text-muted-foreground/70
                             pl-9 pr-10 py-2.5 text-sm
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-card
                             disabled:opacity-60 disabled:cursor-not-allowed
                             transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  aria-pressed={showPassword}
                  disabled={isLoading}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center
                             rounded-md text-muted-foreground hover:text-foreground hover:bg-muted
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40 focus-visible:ring-offset-2 focus-visible:ring-offset-card
                             transition-colors"
                >
                  {showPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !username.trim() || !password.trim()}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl
                         bg-primary text-primary-foreground font-medium text-sm
                         px-4 py-2.5
                         shadow-[0_4px_16px_hsl(var(--primary)/0.35),inset_0_1px_0_rgba(255,255,255,0.25)]
                         hover:shadow-[0_6px_24px_hsl(var(--primary)/0.5),inset_0_1px_0_rgba(255,255,255,0.3)]
                         active:scale-[0.99]
                         disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-card
                         transition-all duration-200"
            >
              {isLoading ? (
                <>
                  <FiLoader className="h-4 w-4 animate-spin" aria-hidden />
                  Entrando…
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>
        </section>

        {/* Rodapé fora do card — peso visual mais leve */}
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Autenticação segura · Sessão válida por 7 dias
        </p>
      </main>
    </div>
  )
}

export default AuthLogin
